import React from "react";
import { Box, Typography, Button, Tooltip } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PropTypes from "prop-types";

import { formatDate } from "../../../utils/function";
import LazyImage from "../../../utils/LazyImage";
import Chapter from "../Chapter";
import RelevanceScore from "../RelevanceScore";

const ListViewCard = ({
  item,
  content,
  handleOpenSummary,
  handleNavigate,
  isMobile,
  styles,
  fallbackImage,
  handleAddFavourite,
  imageUrl,
  cardButtonDisable,
  isFavourite,
  chapter,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: isMobile ? "8px" : "16px",
        background: styles?.insideCardBackgroundColorLayer1,
        borderRadius: "8px",
        // boxShadow: `0px 0px 24px 0px ${styles?.boxShadowlayerColor1}`,
        width: "100%", // Ensures the list is 100% width of the container
        boxShadow: "none",
        border: "1px solid #D7E5F8",
      }}
    >
      {isMobile && (
        <Tooltip title="Relevance Score" arrow>
          <Typography sx={{ fontWeight: 600 }}>
            {item.relevanceScore}
          </Typography>
        </Tooltip>
      )}
      {/* Thumbnail + Details */}
      <div className="flex flex-row gap-[24px]">
        {!isMobile && (
          <Box sx={{ position: "relative", height: "100%" }}>
            <LazyImage
              url={item?.cdnUrl}
              alt="Thumbnail"
              className="h-full w-full rounded-[8px]"
              height={140}
            />

            {/* Overlay content */}
            {chapter && <RelevanceScore card={item} />}
          </Box>
        )}

        <div className="flex flex-col" style={{ flex: 1 }}>
          <Typography
            sx={{
              color: styles?.textColorLayer2,
              fontWeight: 700,
              fontSize: isMobile ? "18px" : "1.2rem",
              lineHeight: "32px",
              fontFamily: "Inter",
              marginBottom: "8px",
            }}
          >
            {item?.title}
          </Typography>
          <Typography
            sx={{
              color: styles?.textColorLayer2,
              fontWeight: 500,
              fontSize: isMobile ? "12px" : "16px",
              lineHeight: "20px",
              fontFamily: "Inter",
              marginBottom: "8px",
            }}
            dangerouslySetInnerHTML={{
              __html: item?.highlights?.summary
                ? item?.highlights?.summary[0]
                : item?.description || "",
            }}
          />

          <div className="flex mb-[12px]">
            <div
              className={`${
                isMobile ? "mr-[8px]" : ""
              } flex flex-row gap-2 mr-[24px]`}
            >
              <Typography
                sx={{
                  color: styles?.textColorLayer2,
                  fontSize: isMobile ? "12px" : "14px",
                  fontFamily: "Inter",
                  fontWeight: 600,
                }}
              >
                {"Publication Date"} :
              </Typography>
              <Typography
                sx={{
                  color: styles?.textColorLayer2,
                  fontSize: isMobile ? "12px" : "14px",
                  fontFamily: "Inter",
                  fontWeight: 400,
                }}
              >
                {chapter
                  ? formatDate(item?.publicationDate)
                  : formatDate(item?.uploadedOn) || formatDate(item?.date)}
              </Typography>
            </div>

            {content === "Documents" && (
              <div
                className={`${
                  isMobile ? "mr-[8px]" : ""
                } flex flex-row gap-2 mr-[24px]`}
              >
                <Typography
                  sx={{
                    color: styles?.textColorLayer2,
                    fontSize: isMobile ? "12px" : "14px",
                    fontFamily: "Inter",
                    fontWeight: 600,
                  }}
                >
                  View Count:
                </Typography>
                <Typography
                  sx={{
                    color: styles?.textColorLayer2,
                    fontSize: isMobile ? "12px" : "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                  }}
                >
                  {item?.counts || item?.views || 0}
                </Typography>
              </div>
            )}

            <div className="flex flex-row gap-2">
              <Typography
                sx={{
                  color: styles?.textColorLayer2,
                  fontSize: isMobile ? "12px" : "14px",
                  fontFamily: "Inter",
                  fontWeight: 600,
                }}
              >
                Likes:
              </Typography>
              <Typography
                sx={{
                  color: styles?.textColorLayer2,
                  fontSize: isMobile ? "12px" : "14px",
                  fontFamily: "Inter",
                  fontWeight: 400,
                }}
              >
                {item?.likes || "0"}
              </Typography>
            </div>
          </div>
          {chapter && <Chapter highlights={item?.highlights} />}

          {/* Actions: Favorite & View Summary */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <div className="flex flex-row gap-3">
              {/* Favorite Button */}
              <Box
                sx={styles?.linkContainer}
                onClick={
                  !cardButtonDisable
                    ? () => handleAddFavourite(item, content)
                    : undefined
                }
              >
                {isFavourite ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 18 17"
                    fill="none"
                  >
                    <path
                      d="M11.3417 6.18366L17.3334 6.70033L12.7917 10.642L14.15 16.5003L9.00002 13.392L3.85002 16.5003L5.21669 10.642L0.666687 6.70033L6.65835 6.19199L9.00002 0.666992L11.3417 6.18366Z"
                      fill="#005CBC"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M18.3334 7.70033L12.3417 7.18366L10 1.66699L7.65835 7.19199L1.66669 7.70033L6.21669 11.642L4.85002 17.5003L10 14.392L15.15 17.5003L13.7917 11.642L18.3334 7.70033ZM10 12.8337L6.86669 14.7253L7.70002 11.1587L4.93335 8.75866L8.58335 8.44199L10 5.08366L11.425 8.45033L15.075 8.76699L12.3084 11.167L13.1417 14.7337L10 12.8337Z"
                      fill="#005CBC"
                    />
                  </svg>
                )}
                <Typography
                  sx={isMobile ? { display: "none" } : styles?.linkTypography}
                >
                  Favorite
                </Typography>
              </Box>

              {/* View Summary */}
              <Box
                sx={styles?.linkContainer}
                onClick={
                  !cardButtonDisable ? () => handleOpenSummary(item) : undefined
                }
              >
                <RemoveRedEyeOutlinedIcon
                  sx={{ fill: "#005CBC", width: "20px", height: "20px" }}
                />
                <Typography
                  sx={isMobile ? { display: "none" } : styles?.linkTypography}
                >
                  View Summary
                </Typography>
              </Box>
            </div>

            {/* View Button */}
            <Button
              sx={styles?.buttonStyle}
              onClick={
                !cardButtonDisable
                  ? () => handleNavigate(item, content.replace(/s$/, ""))
                  : undefined
              }
            >
              <Typography
                sx={{
                  color: "#005CBC",
                  fontWeight: 700,
                  fontSize: "14px",
                  textTransform: "none",
                }}
              >
                View {content.replace(/s$/, "")}
              </Typography>
              {content === "Links" ? (
                <LaunchOutlinedIcon
                  sx={{ fill: "#005CBC", width: "20px", height: "20px" }}
                />
              ) : (
                <ArrowForwardIcon
                  sx={{ fill: "#005CBC", width: "20px", height: "20px" }}
                />
              )}
            </Button>
          </Box>
        </div>
      </div>
    </Box>
  );
};

// Define PropTypes for validation
ListViewCard.propTypes = {
  item: PropTypes.object.isRequired,
  content: PropTypes.string.isRequired,
  handleAddFavourite: PropTypes.func.isRequired,
  handleOpenSummary: PropTypes.func.isRequired,
  handleNavigate: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  styles: PropTypes.object.isRequired,
  fallbackImage: PropTypes.string.isRequired,
};

export default ListViewCard;
